import { useCallback, useMemo } from 'react';
import { orderBy } from 'lodash';
import { COMPANY_NOT_FOUND } from 'common/constants/auth';
import { normalizePinnedItems, PINNED_COMPANIES_KEY, PinnedItemId } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { Text } from 'components/ui';
import { Company } from 'pages/Auth/components/CompanySelector/components/Company';
import { CompaniesListProps } from './CompaniesList.props';

const CompaniesList = ({ filteredCompaniesList }: CompaniesListProps) => {
  const [pinnedCompaniesRaw, setPinnedCompanies] = useLocalStorageByUser<PinnedItemId[]>(PINNED_COMPANIES_KEY, []);
  const pinnedCompanies = useMemo(() => normalizePinnedItems(pinnedCompaniesRaw), [pinnedCompaniesRaw]);

  const updatePinStatus = useCallback(
    (id: number, _title: string, isPinned: boolean) => {
      let nextPinnedOptions: PinnedItemId[];

      if (isPinned) {
        nextPinnedOptions = pinnedCompanies.filter(option => option.value !== id);
      } else {
        nextPinnedOptions = [...pinnedCompanies, { value: id }];
      }

      setPinnedCompanies(nextPinnedOptions);
    },
    [pinnedCompanies, setPinnedCompanies]
  );

  const processedCompanies = useMemo(
    () =>
      filteredCompaniesList.map(company => {
        const isCompanyPinned = Boolean(pinnedCompanies.find(pinnedCompany => pinnedCompany.value === company.id));

        return {
          ...company,
          isPinned: isCompanyPinned,
          isPinnable: true,
          updatePinStatus: (id: number, title: string, isPinned: boolean) => updatePinStatus(id, title, isPinned),
        };
      }),
    [filteredCompaniesList, pinnedCompanies, updatePinStatus]
  );

  const sortedCompanies = useMemo(() => orderBy(processedCompanies, ['isPinned'], ['desc']), [processedCompanies]);

  if (filteredCompaniesList.length === 0) {
    return <Text variant="h4">{COMPANY_NOT_FOUND.toUpperCase()}</Text>;
  }

  return (
    <>
      {sortedCompanies.map(company => (
        <Company
          key={company.id}
          id={company.id}
          isPinned={company.isPinned}
          isPinnable={company.isPinnable}
          name={company.name}
          slug={company.slug}
          updatePinStatus={company.updatePinStatus}
        />
      ))}
    </>
  );
};

export default CompaniesList;
